/**
 * Sentence Scramble Game
 * Arrange words to form correct sentences
 */
import { useState, useEffect } from 'react';

interface SentenceScrambleGameProps {
  sentences: Array<{ english: string; chinese: string }>;
  onComplete: (score: number) => void;
  onExit: () => void;
}

export default function SentenceScrambleGame({ sentences, onComplete, onExit }: SentenceScrambleGameProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [words, setWords] = useState<string[]>([]);
  const [userOrder, setUserOrder] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [showHint, setShowHint] = useState(false);

  const currentSentence = sentences[currentIndex];

  // Scramble words when sentence changes
  useEffect(() => {
    if (currentSentence) {
      const wordList = currentSentence.english.split(' ');
      const scrambled = [...wordList].sort(() => Math.random() - 0.5);
      setWords(scrambled);
      setUserOrder([]);
      setFeedback(null);
      setShowHint(false);
    }
  }, [currentIndex]);

  const handleWordClick = (word: string, index: number) => {
    if (feedback) return;

    // Add word to user order
    setUserOrder([...userOrder, word]);

    // Remove word from available words
    const newWords = [...words];
    newWords.splice(index, 1);
    setWords(newWords);
  };

  const handleUndo = () => {
    if (userOrder.length === 0) return;

    // Move last word back
    const lastWord = userOrder[userOrder.length - 1];
    setWords([...words, lastWord]);
    setUserOrder(userOrder.slice(0, -1));
  };

  const handleCheck = () => {
    const userSentence = userOrder.join(' ');
    const correctSentence = currentSentence.english;

    if (userSentence === correctSentence) {
      setFeedback('correct');
      setScore(s => s + 20);

      setTimeout(() => {
        if (currentIndex < sentences.length - 1) {
          setCurrentIndex(i => i + 1);
        } else {
          onComplete(score + 20);
        }
      }, 1500);
    } else {
      setFeedback('wrong');
      setTimeout(() => setFeedback(null), 2000);
    }
  };

  const handleReset = () => {
    const wordList = currentSentence.english.split(' ');
    const scrambled = [...wordList].sort(() => Math.random() - 0.5);
    setWords(scrambled);
    setUserOrder([]);
    setFeedback(null);
  };

  if (!currentSentence) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">暂无句子数据</p>
        <button onClick={onExit} className="mt-4 text-purple-500">返回</button>
      </div>
    );
  }

  const allDone = currentIndex === sentences.length - 1 && feedback === 'correct';

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-700">
          ← 返回
        </button>
        <div className="text-center">
          <h2 className="text-xl font-bold text-blue-600">🔤 句子排序</h2>
          <p className="text-sm text-gray-500">把单词排成正确的句子</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-blue-600">得分: {score}</p>
          <p className="text-xs text-gray-400">第 {currentIndex + 1}/{sentences.length} 题</p>
        </div>
      </div>

      {/* Chinese hint */}
      <div className="bg-blue-50 rounded-xl p-4 mb-6 text-center">
        <p className="text-gray-600 mb-1">请把下面的句子翻译成英文：</p>
        <p className="text-xl font-bold text-gray-800">{currentSentence.chinese}</p>
      </div>

      {/* Hint button */}
      <div className="text-center mb-4">
        <button
          onClick={() => setShowHint(!showHint)}
          className="text-sm text-gray-400 hover:text-gray-600"
        >
          {showHint ? '🙈 隐藏提示' : '💡 显示提示'}
        </button>
        {showHint && (
          <p className="text-sm text-gray-500 mt-2 animate-bounce-in">
            正确答案：{currentSentence.english}
          </p>
        )}
      </div>

      {/* User's answer area */}
      <div className="min-h-20 bg-white border-2 border-dashed border-gray-200 rounded-xl p-4 mb-4">
        {userOrder.length === 0 ? (
          <p className="text-gray-300 text-center">点击下方单词组成句子</p>
        ) : (
          <div className="flex flex-wrap gap-2 justify-center">
            {userOrder.map((word, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium animate-bounce-in"
              >
                {word}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Available words */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        {words.map((word, idx) => (
          <button
            key={idx}
            onClick={() => handleWordClick(word, idx)}
            disabled={!!feedback}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-blue-50 hover:text-blue-600 transition-all disabled:opacity-50"
          >
            {word}
          </button>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={handleUndo}
          disabled={userOrder.length === 0 || !!feedback}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
        >
          ↩️ 撤销
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
        >
          🔄 重排
        </button>
        <button
          onClick={handleCheck}
          disabled={words.length > 0 || !!feedback}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 disabled:opacity-50"
        >
          ✓ 检查
        </button>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`mt-6 text-center text-lg font-bold animate-bounce-in ${
          feedback === 'correct' ? 'text-green-600' : 'text-red-500'
        }`}>
          {feedback === 'correct' ? '✅ 正确！太棒了！' : '❌ 再试试看！'}
        </div>
      )}

      {/* Completion */}
      {allDone && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center animate-bounce-in">
          <div className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-blue-600 mb-2">全部完成！</h2>
            <p className="text-lg text-gray-600">总分: {score + 20} 分</p>
            <button
              onClick={onExit}
              className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600"
            >
              完成 ✓
            </button>
          </div>
        </div>
      )}
    </div>
  );
}